import Layout from "@/HOC";
import React, { useEffect, useState } from "react";
import styles from "./signUp.module.css";
import { Checkbox, Col, Row, Select } from "antd";
import LabelInputComponent from "@/component/input/labledinput";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import Link from "next/link";
import { SVG } from "@/assets/svg";
import { IMAGES } from "@/assets/images";
import { useFormik } from "formik";
import { signupSchema } from "../../interfaces/signUp.validator";
import { useRouter } from "next/router";
import { registerUser } from "../../utils/apis/auth/signup.api";
import { IRegisterUser } from "../../interfaces/signup.interface";
import {
  getPlanRenewalDate,
  getTimeInterval,
  handleError,
} from "@/utils/common";
import { IPackage } from "@/interfaces/package.interface";
import { createPayment } from "@/utils/apis/payment/create-payment.api";
import { getSinglePackage } from "@/utils/apis/packages/get-single-package.api";
import DefaultLoader from "@/component/loader";
import { Divider } from "antd";
import GoogleSignUp from "@/component/googleSignup";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useAppContext } from "@/context";
import { NextPage } from "next";
import SelectInputField from "@/component/input/select";
import Search from "@/component/searchables/Search";
import { debounce } from "@/utils/common";
import Head from "next/head";
import DefaultSeo from "@/component/seo/DefaultSeo";

const PRIVACY_POLICY_URL =
  process.env.NEXT_PUBLIC_PRIVACY_POLICY_URL ||
  "https://app.termly.io/document/privacy-policy/777d557c-7256-4ea3-a6f5-0ad890661a68";
const T_AND_C_URL =
  process.env.NEXT_PUBLIC_T_AND_C_URL ||
  "https://app.termly.io/document/terms-of-service/4401c095-fdf1-4429-ba72-c9c0f374b040";

const SignUp: NextPage = () => {
  const router = useRouter();

  const currentPageName = router.pathname.replace(/^\//, "").replace(/-/g, " ");
  const formattedPageName =
    currentPageName.charAt(0).toUpperCase() + currentPageName.slice(1);

  const { handleLoginPopupOpen, setUser, fetchUser, handleForgotPopupOpen } =
    useAppContext();

  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoader, setPageLoader] = useState<boolean>(false);

  const Colors = {
    primary: "#557eff",
  };

  const onChange = (e: CheckboxChangeEvent) => {
    setTermsChecked(e.target.checked);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = React.useCallback(
    debounce(() => {
      setPageLoader(false);
    }, 2000),
    []
  );

  const { selectedPackage, packageId } = router.query;

  const parsedPackage = selectedPackage
    ? JSON.parse(selectedPackage as string)
    : null;

  const [selectedPackageObj, setSelectedPackageObj] = useState<IPackage | null>(
    parsedPackage || null
  );

  const formik = useFormik<IRegisterUser>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      password: "",
      role: "USER",
    },
    validationSchema: signupSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (!termsChecked) {
        handleError("Please accept terms and conditions");
        return;
      }
      try {
        setLoading(true);
        if (selectedPackageObj?.amount === "0") {
          const data = {
            ...values,
            packageId: selectedPackageObj._id,
          };
          const res = await registerUser(data);
          if (res.data.success) {
            setUser(res.data.data);
            fetchUser();
            router.replace("/dashboard");
          }
        } else {
          const res = await createPayment({
            amount: selectedPackageObj?.amount,
            name: selectedPackageObj?.packageName,
            planId: selectedPackageObj?._id,
            email: values.email,
            customerName: `${values.firstName} ${values.lastName}`,
          });
          if (res.data.success) {
            // to get the filled details on another page after stripe payment
            localStorage.setItem(
              "userDetails",
              JSON.stringify({
                ...values,
                packageId: selectedPackageObj?._id,
              })
            );

            window.open(res.data.url, "_self");
          }
        }
      } catch (error: any) {
        console.log(error);
        handleError(error?.response?.data?.message || error?.message);
      }
      setLoading(false);
    },
  });

  useEffect(() => {
    debouncedSearch();
  }, []);

  useEffect(() => {
    if (!selectedPackage && packageId) {
      getSinglePackage(packageId as string).then((data) => {
        setSelectedPackageObj(data.data.data);
      });
    }
  }, [packageId, selectedPackage]);

  const handleLoginPopupClose = () => {
    formik.resetForm();
  };

  const handleLogin = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    handleLoginPopupOpen();
  };

  return (
    <div>
      <DefaultSeo
        title="Sign Up"
        description={
          selectedPackageObj
            ? `Sign up for the ${selectedPackageObj.packageName} plan ($${selectedPackageObj.amount}). ${selectedPackageObj.description || ''}`
            : "Sign up for a plan: print specimen labels, export catalog reports and remove advertisements."
        }
      />
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>
      {pageLoader ? (
        <div className={styles.SignUpLoaderBg}>
          <DefaultLoader
            color={Colors.primary}
            active={pageLoader}
            spinSize={48}
          />
        </div>
      ) : (
        <Layout>
          <form onSubmit={formik.handleSubmit} className={`${styles.signup_banners}`}>
            <div className={`${styles.Banner}`}>
              <div className="main">
                <Row gutter={[32, 32]} className="tabletRow">
                  <Col md={12} xs={24}>
                    <div className={`${styles.signUP}`}>
                      <div className={`${styles.sign}`}>
                        <h4
                          style={{
                            color: "var(--main, #557EFF)",
                            textAlign: "center",
                            fontSize: " 35px",
                            fontStyle: "normal",
                            fontWeight: "800",
                            lineHeight: "38px",
                            letterSpacing: "0.66px",
                            margin: "13px",
                          }}
                        >
                          Sign Up
                        </h4>
                      </div>
                      <div>
                        <LabelInputComponent
                          title="First Name*"
                          placeholder="eg. john"
                          type="text"
                          {...formik.getFieldProps("firstName")}
                          className={`${styles.Forgot}`}
                        />
                      </div>
                      {/* Display form errors */}
                      {formik.touched.firstName && formik.errors.firstName && (
                        <div className={`${styles.error}`}>
                          {formik.errors.firstName}
                        </div>
                      )}
                      <div>
                        <LabelInputComponent
                          title="Last Name"
                          placeholder="eg. smith"
                          type="text"
                          {...formik.getFieldProps("lastName")}
                          className={`${styles.Forgot}`}
                        />
                      </div>
                      {formik.touched.lastName && formik.errors.lastName && (
                        <div className={`${styles.error}`}>
                          {formik.errors.lastName}
                        </div>
                      )}
                      <div>
                        <LabelInputComponent
                          title="Email*"
                          placeholder="eg. john@gmail.com"
                          type="text"
                          {...formik.getFieldProps("email")}
                          className={`${styles.Forgot}`}
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <div className={`${styles.error}`}>
                          {formik.errors.email}
                        </div>
                      )}
                      <div>
                        <label
                          style={{
                            color: "#1F4970",
                            fontSize: "16px",
                            fontWeight: "400",
                            margin: "10px 0px ",
                            marginBottom: "12px",
                            display: "block",
                          }}
                        >
                          Country*
                        </label>
                        <Search
                          type="country"
                          title=""
                          placeholder="Select country"
                          value={formik.values.country}
                          className={`${styles.stockForgot}`}
                          onChange={(value) =>
                            formik.setFieldValue("country", value)
                          }
                        />
                      </div>
                      {formik.touched.country && formik.errors.country && (
                        <div className={`${styles.error}`}>
                          {formik.errors.country}
                        </div>
                      )}
                      <div>
                        <LabelInputComponent
                          title="Password*"
                          placeholder="**********"
                          type="password"
                          {...formik.getFieldProps("password")}
                          className={`${styles.Forgot}`}
                        />
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <div className={`${styles.error}`}>
                          {formik.errors.password}
                        </div>
                      )}
                      {/* removed forget password link, as it is not necessary here but left the h6 tag for the sake of styling.*/}
                      {/* <PointerDiv
                      onClick={handleForgotPopupOpen}
                      className={`${styles.forgot}`}
                    >
                      Forgot password?
                    </PointerDiv>{" "} */}
                      <div>
                        <Checkbox onChange={onChange}>
                          <span>
                            {" "}
                            Accept{" "}
                            <Link target="_blank" href={T_AND_C_URL}>
                              <span className={`${styles.condition}`}>
                                Terms & Conditions
                              </span>
                            </Link>
                            and
                            <Link target="_blank" href={PRIVACY_POLICY_URL}>
                              <span className={`${styles.condition}`}>
                                Privacy Policy
                              </span>
                            </Link>
                          </span>
                        </Checkbox>
                      </div>
                      <div>
                        <button
                          type="submit"
                          disabled={loading}
                          className={`${styles.loginButton}`}
                        >
                          {loading ? <DefaultLoader /> : "Sign Up"}
                        </button>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <h6 className={`${styles.notification}`}>
                          {" "}
                          Already have an account?{" "}
                          <Link onClick={handleLogin} href={""}>
                            <span className={`${styles.signUp}`}> Login</span>
                          </Link>
                        </h6>
                      </div>
                      <Divider style={{ borderColor: "#999" }}>
                        <h6 className={`${styles.continue}`}>
                          {" "}
                          Or continue with
                        </h6>
                      </Divider>
                      <GoogleSignUp
                        handleLoginPopupClose={handleLoginPopupClose}
                        selectedPackageObj={selectedPackageObj}
                      />
                    </div>
                  </Col>
                  {selectedPackageObj && (
                    <Col md={12}>
                      <div className={` ${styles.Plan}`}>
                        <h1 className={` ${styles.selected}`}>
                          Your Selected plan
                        </h1>
                        <div className={` ${styles.payPlan}`}>
                          <Col className={`${styles.downgrade}`}>
                            {selectedPackageObj?.tag && (
                              <button className={`${styles.premiumplan}`}>
                                {selectedPackageObj?.tag}
                              </button>
                            )}
                          </Col>
                          <h2 className={`${styles.dollar}`}>
                            ${selectedPackageObj?.amount}{" "}
                            {selectedPackageObj?.amount !== "0" && (
                              <span>
                                /{getTimeInterval(selectedPackageObj?.Tenure)}
                              </span>
                            )}
                          </h2>
                          <h1 className={`${styles.basic}`}>
                            {selectedPackageObj?.packageName}
                          </h1>
                          {selectedPackageObj?.amount !== "0" && (
                            <h6 className={`${styles.doc}`}>
                              Your plan will renew:
                              <span className={`${styles.userSpan}`}>
                                {getPlanRenewalDate(selectedPackageObj?.Tenure)}
                              </span>
                            </h6>
                          )}

                          {(selectedPackageObj?.numberOfMineral ===
                            "unlimited" ||
                            parseInt(selectedPackageObj?.numberOfMineral) >
                              0) && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />
                              {/* Post Mineral(s) ({selectedPackageObj?.numberOfMineral}) */}
                              {selectedPackageObj?.numberOfMineral ===
                              "unlimited"
                                ? "Unlimited Cataloging"
                                : `Post Mineral(s) (${selectedPackageObj?.numberOfMineral})`}
                            </h3>
                          )}

                          {selectedPackageObj?.printSpecimenLabel && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              Print Specimen Label{" "}
                            </h3>
                          )}

                          {selectedPackageObj?.photosPerSpecimen && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              {selectedPackageObj?.photosPerSpecimen} Photos per
                              Specimen
                            </h3>
                          )}
                          {!!selectedPackageObj?.videoPerSpecimen && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              {selectedPackageObj?.videoPerSpecimen} Videos per
                              Specimen
                            </h3>
                          )}

                          {selectedPackageObj?.exportReport && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              Export Catalog Report
                            </h3>
                          )}

                          {selectedPackageObj?.noBanner && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />
                              Remove Advertisements
                            </h3>
                          )}

                          <Col className="subscriptionBtn">
                            <Link href={"./pricing"}>
                              <button className={`${styles.Currents}`}>
                                Change plan
                              </button>
                            </Link>
                          </Col>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          </form>
        </Layout>
      )}
    </div>
  );
};

export default SignUp;
